import type { Object3D } from 'three';
import {
	isSketchCollectionObject,
	isSketchGroup,
} from '../../../mesh/guards';
import type {CollectSource} from '../../../mesh/sketch/ISketchObject';
import type {SketchCollectionObject} from '../../../mesh/sketch/SketchCollectionObject';
import type {SketchGroup} from '../../../mesh/sketch/SketchGroup';
import {
	clearSketchEntityReferenceTag,
	writeSketchEntityReferenceTag,
} from '../model/SketchEntityReferenceIds';
import {
	restoreProjectionResultFromReferenceRecord,
	type SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import { SketchEntityReferenceObjectCleanupService } from '../target/SketchEntityReferenceObjectCleanupService';
import type {SketchEntityReferenceTargetRetirement} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceObjectCleanupService';
import type {SketchEntityReferenceRepositoryWriteScope} from '../repository/SketchEntityReferenceRepository';
import type {SketchEntityReferenceRebuildTransactionPort} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildBatchTransaction';
import {executeSketchEntityReferenceRepositoryWrite} from '../utils/executeSketchEntityReferenceRepositoryWrite';
import type {
	RemoveGeometryReferenceItemResult,
	RemoveGeometryReferencesResult,
	RemoveGeometryReferencesServiceDependencies,
} from '../../../../types/reference/sketch-entity-reference/RemoveGeometryReferencesService';

export type {
	RemoveGeometryReferenceItemResult,
	RemoveGeometryReferenceItemStatus,
	RemoveGeometryReferencesResult,
	RemoveGeometryReferencesRuntime,
	RemoveGeometryReferencesServiceDependencies,
} from '../../../../types/reference/sketch-entity-reference/RemoveGeometryReferencesService';

/**
 * 删除转换实体引用及其运行时关系。
 *
 * 服务提供两个明确业务入口：目标实体被删除时一并清理引用，手动解除关系时保留普通草图实体。
 * 两条路径共享“准备运行时变更 → 提交仓库 → 提交后清理目标”的事务边界。
 */
export class RemoveGeometryReferencesService {
	private readonly objectCleanupService: SketchEntityReferenceObjectCleanupService;

	/**
	 * @param dependencies 当前文档仓库、关系分发器和目标访问能力。
	 */
	public constructor(
		private readonly dependencies: RemoveGeometryReferencesServiceDependencies,
	) {
		this.objectCleanupService = dependencies.objectCleanupService
			?? new SketchEntityReferenceObjectCleanupService();
	}

	/**
	 * 删除引用记录、关系、标记和目标实体。
	 *
	 * @param referenceIds 需要删除的引用 ID。
	 * @returns 逐条结果和删除快照。
	 */
	public removeReferencesWithTargets(
		referenceIds: readonly string[],
	): Promise<RemoveGeometryReferencesResult> {
		return this.execute(referenceIds, true);
	}

	/**
	 * 在阶段 B 定义变更归属下删除已经离开面边界的派生成员。
	 *
	 * @param referenceIds 需要删除的面成员引用。
	 * @param definitionChangeOwnerId 当前 checkpoint 传播 ID。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 删除结果。
	 */
	public removeDerivedReferencesWithTargets(
		referenceIds: readonly string[],
		definitionChangeOwnerId: string,
		isInputCurrent: () => boolean = () => true,
		repository: SketchEntityReferenceRepositoryWriteScope = this.dependencies.repository,
		transaction?: SketchEntityReferenceRebuildTransactionPort,
		batchKey: string = definitionChangeOwnerId,
	): Promise<RemoveGeometryReferencesResult> {
		return this.execute(
			referenceIds,
			true,
			definitionChangeOwnerId,
			isInputCurrent,
			true,
			repository,
			transaction,
			batchKey,
		);
	}

	/**
	 * 解除引用关系但保留目标实体，使其成为普通草图几何。
	 *
	 * @param referenceIds 需要解除的引用 ID。
	 * @returns 逐条结果和删除快照。
	 */
	public detachReferencesKeepingTargets(
		referenceIds: readonly string[],
	): Promise<RemoveGeometryReferencesResult> {
		return this.execute(referenceIds, false);
	}

	/**
	 * 执行共用删除流程。
	 *
	 * @param inputReferenceIds 输入引用 ID。
	 * @param cleanupTargetEntity 仓库提交后是否清理目标实体。
	 * @param definitionChangeOwnerId 阶段 B 派生删除的定义变更归属。
	 * @param requireCompletePreparation 是否要求整批成员全部准备成功后才允许提交。
	 * @returns 删除结果。
	 */
	private async execute(
		inputReferenceIds: readonly string[],
		cleanupTargetEntity: boolean,
		definitionChangeOwnerId?: string,
		isInputCurrent: () => boolean = () => true,
		requireCompletePreparation = false,
		repository: SketchEntityReferenceRepositoryWriteScope = this.dependencies.repository,
		transaction?: SketchEntityReferenceRebuildTransactionPort,
		batchKey: string = definitionChangeOwnerId ?? 'user-removal',
	): Promise<RemoveGeometryReferencesResult> {
		const referenceIds = Array.from(new Set(inputReferenceIds));
		const references = await repository.loadReferences(referenceIds);
		if (!isInputCurrent()) {
			return this.createEmptyResult();
		}
		const referencesById = new Map(references.map((reference) => [reference.referenceId, reference]));
		const itemsById = new Map<string, RemoveGeometryReferenceItemResult>();
		const prepared: Array<{
			reference: SketchEntityReferenceRecord;
			updatedReference?: SketchEntityReferenceRecord;
			targetEntity: Object3D | null;
			diagnostics: ExecutionDiagnostic[];
		}> = [];

		for (const referenceId of referenceIds) {
			const reference = referencesById.get(referenceId);
			if (!reference) {
				itemsById.set(referenceId, {
					referenceId,
					status: 'not-found',
					diagnostics: [createExecutionDiagnostic({
						kind: 'input-invalid',
						level: 'warning',
						message: `未找到待删除的转换实体引用记录: ${referenceId}`,
						referenceId,
					})],
				});
				continue;
			}

			const targetResolution = this.resolveTargetEntity(reference);
			const { targetEntity } = targetResolution;
			if (!cleanupTargetEntity && !targetEntity) {
				itemsById.set(referenceId, {
					referenceId,
					status: 'failed',
					reference,
					diagnostics: [this.createTargetMissingDiagnostic(reference)],
				});
				continue;
			}

			try {
				this.runDefinitionChangeScope(reference, definitionChangeOwnerId, () => {
					this.dependencies.relationAttachDispatcher.detach({ reference, targetEntity });
					if (targetEntity) {
						clearSketchEntityReferenceTag(targetEntity);
					}
				});
				const shouldRetainDetachedBoundaryMember = !cleanupTargetEntity
					&& (
						reference.selectionIntent?.intentKind === 'topology-face'
						|| reference.selectionIntent?.intentKind === 'topology-loop'
					)
					&& Boolean(reference.selectionIntent.topologyFaceSource);
				prepared.push({
					reference,
					updatedReference: shouldRetainDetachedBoundaryMember
						? {
							...reference,
							boundaryMemberState: 'detached',
						}
						: undefined,
					targetEntity,
					diagnostics: targetResolution.diagnostic ? [targetResolution.diagnostic] : [],
				});
			} catch (error) {
				itemsById.set(referenceId, {
					referenceId,
					status: 'failed',
					reference,
					diagnostics: [createExecutionDiagnostic({
						kind: 'cascade-remove-failed',
						level: 'error',
						message: `拆除转换实体引用关系失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId,
						targetEntityId: reference.targetEntityId,
					})],
				});
			}
		}

		/*
		 * 面边界成员替换是一个集合定义变更：任一旧成员无法拆除时，提交其余成员会
		 * 产生“部分旧集合”。因此派生入口在准备阶段即回滚整批，普通手动删除仍保留
		 * 原有逐条成功语义。
		 */
		if (requireCompletePreparation && prepared.length !== referenceIds.length) {
			for (const preparedReference of [...prepared].reverse()) {
				const {reference, targetEntity} = preparedReference;
				const diagnostics = [
					...preparedReference.diagnostics,
					createExecutionDiagnostic({
						kind: 'cascade-remove-failed',
						level: 'error',
						message: '派生边界成员未能完整拆除，已回滚整批删除',
						referenceId: reference.referenceId,
						targetEntityId: reference.targetEntityId,
					}),
					...this.compensatePreparedReference(reference, targetEntity),
				];
				itemsById.set(reference.referenceId, {
					referenceId: reference.referenceId,
					status: 'failed',
					reference,
					diagnostics,
				});
			}
			prepared.length = 0;
		}

		if (prepared.length > 0) {
			try {
				if (!isInputCurrent()) {
					for (const preparedReference of [...prepared].reverse()) {
						this.compensatePreparedReference(
							preparedReference.reference,
							preparedReference.targetEntity,
						);
					}
					return this.createEmptyResult();
				}
				await executeSketchEntityReferenceRepositoryWrite(
					this.dependencies.repository,
					repository,
					isInputCurrent,
					new Error('checkpoint 派生删除已过期'),
					async tx => {
					const updatedReferences = prepared
						.map(({ updatedReference }) => updatedReference)
						.filter((reference): reference is SketchEntityReferenceRecord => Boolean(reference));
					const removedReferenceIds = prepared
						.filter(({ updatedReference }) => !updatedReference)
						.map(({ reference }) => reference.referenceId);
					if (updatedReferences.length > 0) {
						await tx.saveReferences(updatedReferences);
					}
					if (removedReferenceIds.length > 0) {
						await tx.removeReferences(removedReferenceIds);
					}
					},
				);
				prepared.forEach(({ reference, updatedReference, diagnostics }) => {
					itemsById.set(reference.referenceId, {
						referenceId: reference.referenceId,
						status: updatedReference ? 'detached' : 'removed',
						reference,
						updatedReference,
						diagnostics: [...diagnostics],
					});
				});
			} catch (error) {
				for (const preparedReference of [...prepared].reverse()) {
					const { reference, targetEntity } = preparedReference;
					const diagnostics = [...preparedReference.diagnostics, createExecutionDiagnostic({
						kind: 'cascade-remove-failed',
						level: 'error',
						message: `删除转换实体引用记录失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: reference.referenceId,
						targetEntityId: reference.targetEntityId,
					})];
					diagnostics.push(...this.compensatePreparedReference(reference, targetEntity));
					itemsById.set(reference.referenceId, {
						referenceId: reference.referenceId,
						status: 'failed',
						reference,
						diagnostics,
					});
				}
			}
		}

		const items = referenceIds
			.map((referenceId) => itemsById.get(referenceId))
			.filter((item): item is RemoveGeometryReferenceItemResult => Boolean(item));
		const removedReferences = items
			.filter((item) => item.status === 'removed' && item.reference)
			.map((item) => item.reference as SketchEntityReferenceRecord);
		const updatedReferences = items
			.filter((item) => (
				item.status === 'detached'
				&& item.reference
				&& item.updatedReference
			))
			.map((item) => ({
				before: item.reference as SketchEntityReferenceRecord,
				after: item.updatedReference as SketchEntityReferenceRecord,
			}));
		const removedTargetEntityByReferenceId = new Map(
			removedReferences.map(reference => [
				reference.referenceId,
				prepared.find(item => item.reference.referenceId === reference.referenceId)
					?.targetEntity ?? null,
			]),
		);
		const preparedTargetEntityByReferenceId = new Map(
			prepared.map(item => [item.reference.referenceId, item.targetEntity]),
		);
		const removedTargetEntities = [...removedTargetEntityByReferenceId.values()]
			.filter((targetEntity): targetEntity is Object3D => Boolean(targetEntity));
		const removedTargetEntitySet = new Set(removedTargetEntities);
		const retainedCollectionSources = new Map<SketchCollectionObject, CollectSource[]>();
		removedTargetEntities.forEach(targetEntity => {
			const candidateCollection = targetEntity.parent;
			if (
				!isSketchCollectionObject(candidateCollection)
				|| retainedCollectionSources.has(candidateCollection)
			) {
				return;
			}
			retainedCollectionSources.set(
				candidateCollection,
				[...candidateCollection.collectSet].filter(
					source => !removedTargetEntitySet.has(source),
				),
			);
		});
		const removedTargetEntityIds: string[] = [];

		if (cleanupTargetEntity && transaction) {
			const retirementByReferenceId = new Map<
				string,
				SketchEntityReferenceTargetRetirement
			>();
			const retirementSketchByReferenceId = new Map<string, SketchGroup>();
			removedReferences.forEach((reference) => {
				const targetEntity = removedTargetEntityByReferenceId.get(reference.referenceId);
				if (!targetEntity) {
					return;
				}
				const targetSketch = this.findOwnerSketchGroup(targetEntity);
				const retirement = this.runDefinitionChangeScope(
					reference,
					definitionChangeOwnerId,
					() => targetSketch
						? targetSketch.runEntityReplacementScope(
							() => this.objectCleanupService.beginTargetRetirement(targetEntity),
						)
						: this.objectCleanupService.beginTargetRetirement(targetEntity),
				);
				retirementByReferenceId.set(reference.referenceId, retirement);
				if (targetSketch) {
					retirementSketchByReferenceId.set(reference.referenceId, targetSketch);
				}
				removedTargetEntityIds.push(reference.targetEntityId);
			});
			const committedRemovalEntityIdsBySketch = new Map<SketchGroup, string[]>();
			removedReferences.forEach((reference) => {
				const targetSketch = retirementSketchByReferenceId.get(reference.referenceId);
				if (!targetSketch) {
					return;
				}
				const removedEntityIds = committedRemovalEntityIdsBySketch.get(targetSketch) ?? [];
				removedEntityIds.push(reference.targetEntityId);
				committedRemovalEntityIdsBySketch.set(targetSketch, removedEntityIds);
			});
			committedRemovalEntityIdsBySketch.forEach((removedEntityIds, targetSketch) => {
				transaction.recordCommittedEntityRemoval({
					targetSketch,
					targetSketchId: targetSketch.persistentId,
					batchKey,
					removedEntityIds,
				});
			});
			const referencesToRestore = [
				...removedReferences,
				...updatedReferences.map(item => item.before),
			];
			transaction.record({
				targetSketchId: referencesToRestore[0]?.targetSketchId ?? 'unknown-sketch',
				batchKey,
				description: '提交 checkpoint 派生成员退役',
				rollback: async () => {
					const rollbackDiagnostics: ExecutionDiagnostic[] = [];
					for (const reference of [...referencesToRestore].reverse()) {
						try {
							const rollback = () => {
								retirementByReferenceId.get(reference.referenceId)?.rollback();
								rollbackDiagnostics.push(...this.compensatePreparedReference(
									reference,
									preparedTargetEntityByReferenceId.get(reference.referenceId) ?? null,
								));
							};
							const targetSketch = retirementSketchByReferenceId.get(reference.referenceId);
							if (targetSketch) {
								targetSketch.runEntityReplacementScope(rollback);
							} else {
								rollback();
							}
						} catch (error) {
							rollbackDiagnostics.push(createExecutionDiagnostic({
								kind: 'compensation-failed',
								level: 'error',
								message: `回滚派生删除运行时对象失败: ${error instanceof Error ? error.message : String(error)}`,
								targetSketchId: reference.targetSketchId,
								referenceId: reference.referenceId,
								targetEntityId: reference.targetEntityId,
							}));
						}
					}
					try {
						await repository.saveReferences(referencesToRestore);
					} catch (error) {
						rollbackDiagnostics.push(createExecutionDiagnostic({
							kind: 'compensation-failed',
							level: 'error',
							message: `回滚派生删除仓储记录失败: ${error instanceof Error ? error.message : String(error)}`,
							targetSketchId: referencesToRestore[0]?.targetSketchId,
							referenceId: referencesToRestore[0]?.referenceId,
						}));
					}
					if (rollbackDiagnostics.some(item => item.level === 'error')) {
						throw new Error(rollbackDiagnostics.map(item => item.message).join('; '));
					}
					return rollbackDiagnostics;
				},
				finalize: () => {
					removedReferences.forEach((reference) => {
						const commit = () => retirementByReferenceId
							.get(reference.referenceId)
							?.commit();
						const targetSketch = retirementSketchByReferenceId.get(reference.referenceId);
						if (targetSketch) {
							targetSketch.runEntityReplacementScope(commit);
						} else {
							commit();
						}
					});
					retainedCollectionSources.forEach((retainedSources, collection) => {
						const targetSketch = this.findOwnerSketchGroup(collection);
						if (targetSketch) {
							targetSketch.runEntityReplacementScope(
								() => collection.setSources(retainedSources),
							);
						} else {
							collection.setSources(retainedSources);
						}
					});
				},
			});
		} else if (cleanupTargetEntity) {
			for (const reference of removedReferences) {
				const targetEntity = removedTargetEntityByReferenceId.get(
					reference.referenceId,
				);
				if (!targetEntity) {
					continue;
				}
				try {
					this.runDefinitionChangeScope(reference, definitionChangeOwnerId, () => {
						this.objectCleanupService.cleanupTargetEntity(targetEntity);
					});
					removedTargetEntityIds.push(reference.targetEntityId);
				} catch (error) {
					const resultItem = items.find((item) => item.referenceId === reference.referenceId);
					resultItem?.diagnostics.push(createExecutionDiagnostic({
						kind: 'target-dispose-failed',
						level: 'error',
						message: `引用记录已删除，但目标实体资源释放失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: reference.referenceId,
						targetEntityId: reference.targetEntityId,
					}));
				}
			}

			/**
			 * Face/Wire 成员可以继续逐条清理 relation、tag、实体与资源，但闭合集合必须等
			 * 整批目标清理完成后再按最终成员重建一次，避免 4→3→2 的中间轮廓重绑。
			 */
			retainedCollectionSources.forEach((retainedSources, collection) => {
				try {
					collection.setSources(retainedSources);
				} catch (error) {
					removedReferences.forEach(reference => {
						const targetEntity = removedTargetEntityByReferenceId.get(
							reference.referenceId,
						);
						if (!targetEntity || !collection.collectSet.has(targetEntity as CollectSource)) {
							return;
						}
						const resultItem = items.find(item => (
							item.referenceId === reference.referenceId
						));
						resultItem?.diagnostics.push(createExecutionDiagnostic({
							kind: 'target-dispose-failed',
							level: 'error',
							message: `引用记录已删除，但闭合 Wire 最终成员提交失败: ${
								error instanceof Error ? error.message : String(error)
							}`,
							referenceId: reference.referenceId,
							targetEntityId: reference.targetEntityId,
						}));
					});
				}
			});
		}

		const result = {
			items,
			removedReferenceIds: removedReferences.map((reference) => reference.referenceId),
			removedReferences,
			updatedReferences,
			removedTargetEntityIds,
			diagnostics: items.flatMap((item) => item.diagnostics),
		};
		return result;
	}

	/**
	 * 创建派生删除任务已经过期时的无副作用结果。
	 *
	 * @returns 不包含删除、更新或诊断的结果。
	 */
	private createEmptyResult(): RemoveGeometryReferencesResult {
		return {
			items: [],
			removedReferenceIds: [],
			removedReferences: [],
			updatedReferences: [],
			removedTargetEntityIds: [],
			diagnostics: [],
		};
	}

	/**
	 * 在可选的阶段 B owner 作用域内执行目标草图变更。
	 *
	 * @param reference 当前引用。
	 * @param ownerId 定义变更归属。
	 * @param work 同步变更。
	 * @returns 工作结果。
	 */
	private runDefinitionChangeScope<T>(
		reference: SketchEntityReferenceRecord,
		ownerId: string | undefined,
		work: () => T,
	): T {
		if (!ownerId || !this.dependencies.runDefinitionChangeScope) {
			return work();
		}
		return this.dependencies.runDefinitionChangeScope(
			reference.targetSketchId,
			ownerId,
			work,
		);
	}

	/**
	 * 从目标实体当前父链定位所属草图。
	 *
	 * @param targetEntity 仍位于正式场景中的目标实体或闭合集合。
	 * @returns 命中的目标草图；实体尚未入场时返回 null。
	 */
	private findOwnerSketchGroup(targetEntity: Object3D): SketchGroup | null {
		let current: Object3D | null = targetEntity;
		while (current) {
			if (isSketchGroup(current)) {
				return current;
			}
			current = current.parent;
		}
		return null;
	}

	/**
	 * 解析引用目标；目标已经被外部删除时允许返回 null。
	 *
	 * @param reference 引用记录。
	 * @returns 当前目标对象或 null。
	 */
	private resolveTargetEntity(reference: SketchEntityReferenceRecord): {
		targetEntity: Object3D | null;
		diagnostic: ExecutionDiagnostic | null;
	} {
		try {
			const targetEntity = this.dependencies.runtime.getSketchObject(
				reference.targetSketchId,
				reference.targetEntityId,
			);
			return {
				targetEntity,
				diagnostic: targetEntity ? null : createExecutionDiagnostic({
					kind: 'target-missing-cleanup',
					level: 'warning',
					message: `删除转换实体引用时目标实体已经离场: ${reference.targetSketchId}/${reference.targetEntityId}`,
					referenceId: reference.referenceId,
					targetEntityId: reference.targetEntityId,
				}),
			};
		} catch (error) {
			return {
				targetEntity: null,
				diagnostic: createExecutionDiagnostic({
					kind: 'target-missing-cleanup',
					level: 'error',
					message: `删除转换实体引用时读取目标实体失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: reference.referenceId,
					targetEntityId: reference.targetEntityId,
				}),
			};
		}
	}

	/**
	 * 仓库提交失败后恢复 tag 和 solver relation。
	 *
	 * @param reference 删除前记录。
	 * @param targetEntity 删除前目标对象。
	 * @returns 补偿阶段诊断。
	 */
	private compensatePreparedReference(
		reference: SketchEntityReferenceRecord,
		targetEntity: Object3D | null,
	): ExecutionDiagnostic[] {
		if (!targetEntity) {
			return [createExecutionDiagnostic({
				kind: 'compensation-failed',
				level: 'error',
				message: `删除事务失败后无法恢复缺失的目标实体: ${reference.targetEntityId}`,
				referenceId: reference.referenceId,
				targetEntityId: reference.targetEntityId,
			})];
		}

		const diagnostics: ExecutionDiagnostic[] = [];
		writeSketchEntityReferenceTag(targetEntity, {
			referenceId: reference.referenceId,
			isReferencedEntity: true,
		});
		try {
			const attachResult = this.dependencies.relationAttachDispatcher.attach({
				reference,
				projectionResult: restoreProjectionResultFromReferenceRecord(reference),
				targetEntity,
				runtimeSourceName: reference.sourceDisplayLabel,
			});
			diagnostics.push(...attachResult.diagnostics);
			if (!attachResult.record || attachResult.diagnostics.some((diagnostic) => diagnostic.level === 'error')) {
				diagnostics.push(createExecutionDiagnostic({
					kind: 'compensation-failed',
					level: 'error',
					message: `删除事务失败后恢复引用关系失败: ${reference.referenceId}`,
					referenceId: reference.referenceId,
					targetEntityId: reference.targetEntityId,
				}));
			}
		} catch (error) {
			diagnostics.push(createExecutionDiagnostic({
				kind: 'compensation-failed',
				level: 'error',
				message: `删除事务失败后恢复引用关系异常: ${error instanceof Error ? error.message : String(error)}`,
				referenceId: reference.referenceId,
				targetEntityId: reference.targetEntityId,
			}));
		}
		return diagnostics;
	}

	/**
	 * 构造保留目标模式下的目标缺失诊断。
	 *
	 * @param reference 引用记录。
	 * @returns 目标缺失诊断。
	 */
	private createTargetMissingDiagnostic(reference: SketchEntityReferenceRecord): ExecutionDiagnostic {
		return createExecutionDiagnostic({
			kind: 'target-missing-cleanup',
			level: 'error',
			message: `保留目标解除引用时未找到目标实体: ${reference.targetSketchId}/${reference.targetEntityId}`,
			referenceId: reference.referenceId,
			targetEntityId: reference.targetEntityId,
		});
	}
}
